import { Storage, API } from 'aws-amplify';
import { getPrizesForConfig } from '../../../graphql/queries';
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { useEffect, useState, useRef, SetStateAction, Dispatch } from 'react';
import moment from 'moment-timezone';
import { mechanicForDisplay } from '../../../constants/lists';
import {
  configurationData,
  configWithPrizes,
  AssignedPromosCustomHookType,
  listAssignedPromosPropsType
} from '../../../types/componentTypes/assignedPromotions';
import { ConfigType } from '../../../types/configuration';
import { GetPrizesTableResult } from '../../../types/componentTypes/PrizeTypes';
import { useUser } from '../../../providers/UserContext';
import { rolesMap } from '../../../constants/roles-constants';
import { useCountries } from '../../../hooks/useCountries';
import { getMechanicFlow } from '../../../utils/configUtils';
import { handleAuthError } from '../../../utils/authError';

Storage.configure({
  customPrefix: {
    public: '',
    protected: '',
    private: '',
  },
});


const useIsInitialQuery = () => {
  const initialQuery = useRef(true);

  useEffect(() => {
    initialQuery.current = false;
  }, []);

  return initialQuery.current;
}

export const useGetPrizesInfo = ({ listAssignedPromosProps, setNotificationState, campaignConfigsData }: AssignedPromosCustomHookType) => {
  const initialQuery = useIsInitialQuery();

  const [configPrizes, setConfigPrizes] = useState<configWithPrizes[]>([
    { configurationId: '', prizesArray: [] },
  ]);

  const [prizeLoading, setPrizeLoading] = useState(true);

  useEffect(() => {
    const getPrizes = async (configurationInput: string) => {
      const queryResult = await API.graphql({
        query: getPrizesForConfig,
        variables: { configuration_id: configurationInput },
      }) as GraphQLResult<GetPrizesTableResult>;
      return queryResult?.data?.getPrizesForConfig?.items;
    };

    const getPrizesForConfiguration = async (configurationList: string[]) => {
      try {
        const results = await Promise.all(
          configurationList.map(async (configuration) => {
            const prizes = await getPrizes(configuration);
            return {
              configurationId: configuration,
              prizesArray: prizes,
            };
          })
        );

        setConfigPrizes(results);
      } catch (e) {
        if (!handleAuthError(e)) {
            setNotificationState({
                open: true,
                title: 'Loading prizes request failed!',
                content: '',
                level: 'error',
            });
            console.error('Loading prizes failed with: ', e);
        }
      }
    }

    if (
      listAssignedPromosProps.data.configurations &&
      listAssignedPromosProps.data.configurations.length > 0 &&
      initialQuery
    ) {
      getPrizesForConfiguration(listAssignedPromosProps.data.configurations).then(() => {
        setPrizeLoading(false);
      });
    }

    // re-fetch prizes for configs after copying promos
    if (!initialQuery && campaignConfigsData && campaignConfigsData.length) {
      const promotionConfigurationIds = campaignConfigsData.map(promo => promo.promotionConfigurationId);
      getPrizesForConfiguration(promotionConfigurationIds).then(() => {
        setPrizeLoading(false);
      });
    }
  }, [listAssignedPromosProps, initialQuery, campaignConfigsData, setNotificationState])

  return {
    configPrizes, prizeLoading
  }
}

export const convertConfigDataForListing = (config) => {
  const configName =
    config?.configurationParameters?.additionalInformation
      ?.name;
  let startTime: string;
  let endTime: string;
  if (config.configurationParameters.configurationStartUtc) {
    const dateTime = moment(
      config.configurationParameters.configurationStartUtc
    ).format('DD.MM.YYYY');
    startTime = dateTime;
  }
  if (config.configurationParameters.configurationEndUtc) {
    const dateTime = moment(
      config.configurationParameters.configurationEndUtc
    ).format('DD.MM.YYYY');
    endTime = dateTime;
  }

  const mechanic =
    Object.keys(mechanicForDisplay)[
    Object.values(mechanicForDisplay).reduce(
      (accumulator, currentValue, index) => {
        if (JSON.stringify(config).includes(currentValue)) {
          return accumulator + index;
        }
        return accumulator;
      }, 0)];

  return {
    promotionName: configName
      ? config.configurationParameters.additionalInformation
        .name
      : 'Not available',
    promotionConfigurationId: config.configurationId,
    market: config.configurationParameters.country
      ? config.configurationParameters.country
      : 'Not available',
    startDate: config.configurationParameters
      .configurationStartUtc
      ? startTime
      : 'Not available',
    endDate: config.configurationParameters
      .configurationEndUtc
      ? endTime
      : 'Not available',
    mechanic: mechanic,
  };
}


export const useAssignedPromotionsList = ({ listAssignedPromosProps, setNotificationState }: AssignedPromosCustomHookType) => {
  const { userMaxRole, userMarket } = useUser();
  const { getCountryCodes } = useCountries();
  const initialQuery = useIsInitialQuery();
  const [loading, setLoading] = useState(true);
  const [s3PromotionsList, sets3PromotionsList] = useState<
    configurationData[]
  >([]);

  useEffect(() => {
    if (
      listAssignedPromosProps.data.configurations &&
      listAssignedPromosProps.data.configurations.length > 0 &&
      initialQuery
    ) {

      getS3configurations(listAssignedPromosProps.data.configurations).then((data) => {
        sets3PromotionsList(data);
        setLoading(false);
      })
    }
    if (!listAssignedPromosProps.data.configurations || !listAssignedPromosProps.data.configurations.length) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listAssignedPromosProps.data.configurations, listAssignedPromosProps.input, initialQuery]);

  const retrieveConfigData = async (configuration: string): Promise<ConfigType> => {
    try {
      const configResult: any = await Storage.get(
        `${configuration}/conf.txt`,
        { download: true, cacheControl: 'no-cache' }
      );
      const result = await configResult.Body.text();
      return JSON.parse(result);
    } catch (err) {
      console.error('Error getting confiugration data', err);
      setNotificationState({
        open: true,
        title: 'Error',
        content: `Config id ${configuration} not found in s3!`,
        level: 'error',
      });
      setLoading(false);
    }
  };

  const getS3configurations = async (configurationList: string[]): Promise<configurationData[]> => {
    const configurationData: configurationData[] = [];
    for (const configuration of configurationList) {
      const parseConfiguration = await retrieveConfigData(configuration);

      // only show the promotions for specific market(s) for promotion managers.
      if (userMaxRole >= rolesMap.PromotionManager) {
        // Only C&G and Auto C&G promos should be visible to prize managers.
        if (userMaxRole === rolesMap.PrizeManager) {
          const mechanic = getMechanicFlow(parseConfiguration);
          if (mechanic !== 'redeemPincodeForCurrencies' && mechanic !== 'autoRedeemCnG') {
            continue;
          }
        }

        const countryValue = getCountryCodes(
          parseConfiguration.configurationParameters?.country
        );
        if (!userMarket.includes(countryValue)) {
          continue;
        }
      }

      const configurationMetadata = convertConfigDataForListing(parseConfiguration);
      configurationData.push(configurationMetadata);
    }
    return configurationData;
  };

  return {
    loading, s3PromotionsList
  }

}

export const useFilteredList = (s3PromotionsList: configurationData[], listAssignedPromosProps: listAssignedPromosPropsType) => {
  const [assignedPromotionsList, setAssignedPromotionsList] = useState<configurationData[]>([]);

  useEffect(() => {
    const filteredData = s3PromotionsList.filter((data: any) => {
      const includesData = listAssignedPromosProps.input.toLowerCase();
      if (listAssignedPromosProps.input === '') {
        return data;
      } else if (
        String(data.promotionName).toLowerCase().includes(includesData) ||
        (data.promotionConfigurationId || '')
          .toLowerCase()
          .includes(includesData) ||
        (data.market || '').toLowerCase().includes(includesData) ||
        (data.startDate || '').toLowerCase().includes(includesData) ||
        (data.endDate || '').toLowerCase().includes(includesData) ||
        (data.mechanic || '').toLowerCase().includes(includesData)
      ) {
        return data;
      }
      return false;
    });

    setAssignedPromotionsList(filteredData);

  }, [s3PromotionsList, listAssignedPromosProps.input])

  return assignedPromotionsList
}

export const usePaginatedData = (assignedPromotionsList: configurationData[], rowsPerPage: number, page: number, setPage: Dispatch<SetStateAction<number>>) => {
  const [paginatedPromoList, setPaginatedPromoList] = useState<configurationData[][]>([[]]);
  useEffect(() => {
    const chunkDataForPages = (
      inputArray: configurationData[],
      lengthOfChunk: number
    ) => {
      const chunks = [];
      for (let i = 0; i < inputArray.length;) {
        chunks.push(inputArray.slice(i, (i += lengthOfChunk)));
      }
      if (page >= chunks.length) setPage(chunks.length > 1 ? chunks.length - 1 : 0);
      return chunks;
    };

    setPaginatedPromoList(
      chunkDataForPages(assignedPromotionsList, rowsPerPage)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedPromotionsList, rowsPerPage]);

  return paginatedPromoList
}
